var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, css, nothing } from 'lit';
import { property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { arrayDecoder, booleanDecoder, customElement, numberDecoder, PICInput, PICModal } from '../Internals';
let PICColorPicker = class PICColorPicker extends PICInput {
    constructor() {
        super();
        /** preset when no presets are chosen  */
        this.presets = [
            '#980000',
            '#ff0000',
            '#ff9900',
            '#ffff00',
            '#00ff00',
            '#00ffff',
            '#4a86e8',
            '#0000ff',
            '#9900ff',
            '#ff00ff',
            '#000000',
            '#404040',
            '#808080',
            '#c0c0c0',
            '#ffffff'
        ];
        /** Only presets to choose from, no ordinary color picker */
        this.presetsOnly = false;
        this.historyLength = 5;
        this.isVisible = false;
        this.NO_COLOR = '#000001';
        this.colorHistory = [];
    }
    get submitValue() {
        if (this.value === this.NO_COLOR)
            return '';
        if (!this.inputField || !this.presetsColorDisplay) {
            return this.value;
        }
        return this.inputField.value;
    }
    static get styles() {
        const defaultStyle = css `
        :host {
            --pic-colorpickerMenuArrowDownColor: var(--pic-onSurfaceMediumColor);
            --pic-arrowBottom: 0px;
            --pic-colorpickerBorderOffset: 2px;
            --pic-colorpickerDensityLowInputHeight: var(--pic-densityLowInputHeight);
            --pic-colorpickerDensityMediumInputHeight: var(--pic-densityMediumInputHeight);
            --pic-colorpickerDensityHighInputHeight: var(--pic-densityHighInputHeight);
            --pic-colorpickerDensityInlineInputHeight: var(--pic-densityInlineInputHeight);
        }
        label {
            z-index: 1
        }
        .colorPicker {
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 0;
            border-radius: 4px;
            cursor: pointer;
            background-color: var(--pic-surfaceColor, white);
            box-sizing: border-box;
            border: 1px solid var(--pic-innerInputBorderColor);
            width: 100%;
            position: relative;
        }
        .colorPicker:focus {
            border: 2px solid var(--pic-innerInputBorderFocusColor);
        }
        .btnSizeOld, .btnSizeMedium {
            height: var(--pic-colorpickerDensityMediumInputHeight);
        }
        .btnSizeLow {
            height: var(--pic-colorpickerDensityLowInputHeight);
        }
        .btnSizeHigh {
            height: var(--pic-colorpickerDensityHighInputHeight);
        }
        .btnSizeInline {
            height: var(--pic-colorpickerDensityInlineInputHeight);
        }
        .inner-input {
            border: none;
            padding: 0;
            box-sizing: border-box;
            font-family: monospace;
            visibility: hidden;
            width: 0 !important;
            height: 0 !important;
            margin: 0 !important;
        }
        .densityDefault {
            width: 100%;
            height: 40%;
            border-radius: 4px;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        .densityOldMediumLow {
            position: absolute;
            height: 25% !important;
        }
        .densityHigh {
            position: absolute;
            height: 30% !important;
        }
        #inputColor {
            height: 100%;
            width: 70%;
            border-radius: 8px;
            margin-left: 5%;
            border: 1px solid var(--pic-outline, #515151);
        }
        .arrow {
            width: 25%;
        }
        .arrow::after {
            content: '\u{F035D}';
            font-family: "Material Design Icons";
            font-size: x-large;
            color: var(--pic-colorpickerMenuArrowDownColor);
            bottom: var(--pic-arrowBottom);
            position: relative;
            line-height: 2;
        }
 
        /* Presets only */
        .presetsColors {
            display: flex;
            flex-wrap: wrap;
            flex-direction: row;
            align-items: center;
            gap: 6px;
            width: 100%;
           
            box-sizing: border-box;
            height: auto;
            margin: 0;
            padding: 16px;
            list-style: none;
            border: 1px solid var(--pic-outline, lightgray);
            border-radius: 4px;
            position: absolute;
            background-color: var(--pic-surfaceColor, white);
            z-index: 2;
            top: 100%;
        }
        .presetsColors input {
            width: 20px !important;
            height: 20px !important;
            visibility: visible !important;
            border-radius: 2px;
            border: 1px solid var(--pic-outline, #515151);
            box-sizing: border-box;
            -moz-box-sizing: border-box;
            -webkit-box-sizing: border-box;
            font-size: 0;
            cursor: pointer;
            box-shadow: rgba(149, 157, 165, 0.2) 0px 8px 24px;
        }
        .presetsColors input:hover {
            border: 2px solid var(--pic-onSurfaceHighColor, black);
            box-sizing: border-box;
            -moz-box-sizing: border-box;
            -webkit-box-sizing: border-box;
        }
        .presetsColorDisplay {
            position: absolute;
            height: 30% !important;
            width: 100% !important;
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-radius: 4px;
            visibility: visible
        }
        .hidden {
            display: none;
        }
        `;
        return css `
            ${super.styles}
            ${defaultStyle}
        `;
    }
    connectedCallback() {
        super.connectedCallback();
        window.addEventListener('click', this.closePresetsContainer.bind(this));
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener('click', this.closePresetsContainer);
    }
    closePresetsContainer(inputEvent) {
        if (inputEvent.composedPath().find(x => x === this.presetsColors || x === this.presetBtn)) {
        }
        else {
            this.isVisible = false;
            this.requestUpdate();
        }
    }
    /**
     * Use this to reset value to 'blank' / NO_COLOR,
     * //TODO currently missing fx icon to actually make feature available
     */
    setValueToBlank() {
        this.value = this.NO_COLOR;
    }
    getBtnClassInfo() {
        return {
            'colorPicker': true,
            'btnSizeOld': this.density === 'old',
            'btnSizeMedium': this.density === 'old',
            'btnSizeLow': this.density === 'low',
            'btnSizeHigh': this.density === 'high',
            'btnSizeInline': this.density === 'inline'
        };
    }
    getColorPickerClass() {
        return {
            'presetsColors': true,
            'hidden': !this.isVisible
        };
    }
    getPreviewColorStyle() {
        let styles = [];
        if (this.value && this.value !== this.NO_COLOR) {
            styles.push({ 'background': this.value });
        }
        else {
            styles.push({ 'background': 'repeating-linear-gradient(45deg, #000000 5px, #000000 12px, #ffffff 10px, #ffffff 17px)' });
        }
        return styles.length > 0 ? styles.reduce((total, current) => total = Object.assign(Object.assign({}, total), current)) : {};
    }
    getColorPosition() {
        return {
            'densityDefault': true,
            'densityOldMediumLow': this.density === 'old' || this.density === 'medium' || this.density === 'low',
            'densityHigh': this.density === 'high'
        };
    }
    handleBtnEvent() {
        var _a;
        this.inputField.click();
        this.requestUpdate();
        //removes the overlay in a modal, to better color choice
        if (this.parent instanceof PICModal) {
            let overlay = (_a = this.parent.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('.overlay');
            if (overlay) {
                this.addEventListener('focus', () => {
                    overlay.style.backgroundColor = 'transparent';
                });
                this.addEventListener('blur', () => {
                    overlay.style.backgroundColor = 'var(--pic-scrimColor, #9b9b9b80)';
                });
            }
        }
    }
    focus() {
        this.outerBtn.focus();
    }
    doNothing(e) { return false; }
    validate() { super.validate(); }
    changeHandler(e) { return super.changeHandler(e); }
    firstUpdated() {
        var _a;
        if (this.value === '') {
            this.value = this.NO_COLOR;
        }
        if (this.presetsOnly)
            this.historyLength = 0;
        this.colorHistory = (_a = this.colorHistorySession) !== null && _a !== void 0 ? _a : [];
        for (let i = this.colorHistory.length; i < this.historyLength; i++) { //fill with white
            this.colorHistory[i] = '#FFFFFF';
        }
        this.initValue_ = this.submitValue;
        if (this.focus_)
            this.focus();
    }
    get colorHistorySession() {
        let history = sessionStorage.getItem(`HistoryArray-${this.id}`);
        if (history)
            return JSON.parse(history);
        return [];
    }
    set colorHistorySession(colorHistory) {
        sessionStorage.setItem(`HistoryArray-${this.id}`, JSON.stringify(colorHistory));
    }
    BtnEvent() {
        this.isVisible = !this.isVisible;
        this.requestUpdate();
    }
    updateColor(event) {
        this.value = (event === null || event === void 0 ? void 0 : event.target).value;
        this.isVisible = false;
        if (!this.presetsOnly) {
            this.updateColorHistory(this.value);
        }
        this.requestUpdate();
        this.changeHandler();
    }
    renderPresetColors() {
        return html `
            <ul class=${classMap(this.getColorPickerClass())}>
                ${this.getPresets()}
            </ul>
        `;
    }
    // creates a color block from earch elements in arrays plus one for each new chosen color
    //id always same?
    getPresets() {
        let presets = this.presets.map((color) => {
            return html `
                <input
                @click=${this.updateColor}
                style='background-color:${color};'
                type='button'
                value=${color}
                id=${this.id_ + '_inner'}
                >
            `;
        });
        return html `
            ${presets}
        `;
    }
    // Normal colorpicker -> when you choose a new color, add that color to the list.
    // When there are more than `historyLength` new colors, the chosen colors will get overwritten
    updateColorHistory(value) {
        this.colorHistory.unshift(value);
        if (this.colorHistory.length > this.historyLength)
            this.colorHistory.pop();
        this.colorHistorySession = this.colorHistory;
    }
    handleColorHistory() {
        let history = this.colorHistory.map(color => {
            return html ` <option value=${color}>${color}</option>`;
        });
        let presets = this.presets.map((color) => {
            return html ` <option value=${color}>${color}</option>`;
        });
        return html `
            <datalist id="history">${history}${presets}</datalist>
        `;
    }
    colorPickerNormal() {
        return html `
            <div
                style=${styleMap(this.getContainerStyle())}
                class=${classMap(this.getContainerClassMap())}
                id="colorPicker"
            >
                <label
                    class=${classMap(this.getInputLabelClass())}
                    for=${this.id_ + '_inner'}
                >
                    ${this.label}
                </label>
                <button
                    class=${classMap(this.getBtnClassInfo())}
                    @click=${this.handleBtnEvent}
                >
                    <input
                        name="${this.id_}"
                        id=${this.id_ + '_inner'}
                        type="color"
                        list="history"
                        class=${classMap(this.getInnerInputClass())}
                        ?disabled=${this.disabled_}
                        placeholder=${this.placeholder}
                        .value=${this.value}
                        @input=${this.validate}
                        @focusout=${this.changeHandler}
                        @drop=${this.doNothing}
                        @dragenter=${this.doNothing}
                        @dragover=${this.doNothing}
                        @keydown=${this.enterSubmit}
                        @change=${this.updateColor}
                    >
                    ${this.renderErr(this.showFullError)}
                    <div class=${classMap(this.getColorPosition())}>
                        <div style=${styleMap(this.getPreviewColorStyle())} id="inputColor"></div>
                        <div class="arrow"></div>
                    </div>
                </button>
                ${this.handleColorHistory()}
            </div>
            `;
    }
    colorPickerPresets() {
        return html `
            <!-- Presets only -->
            <div
                style=${styleMap(this.getContainerStyle())}
                class=${classMap(this.getContainerClassMap())}
                id="presetsColorPicker"
            >
                <label
                    class=${classMap(this.getInputLabelClass())}
                    for=${this.id_ + '_inner'}
                >
                    ${this.label}
                </label>
                <button
                    class=${classMap(this.getBtnClassInfo())}
                    id='presetBtn'
                    @click=${this.BtnEvent}
                    @change=${this.updateColor}
                >
                    ${this.renderErr(this.showFullError)}
                    <div class='presetsColorDisplay'>
                        <div style=${styleMap(this.getPreviewColorStyle())} id="inputColor"></div>
                        <div class="arrow"></div>
                    </div>
                </button>
                ${this.isVisible ? this.renderPresetColors() : nothing}
            </div>
        `;
    }
    render() {
        this.setComponentInlineStyle();
        return html `
            ${this.presetsOnly ? this.colorPickerPresets() : this.colorPickerNormal()}
            ${this.tooltip_ ? html `<pic-tooltip>${this.tooltip_}</pic-tooltip>` : ''}
        `;
    }
};
__decorate([
    query('.presetsColorDisplay')
], PICColorPicker.prototype, "presetsColorDisplay", void 0);
__decorate([
    query('#inputColor')
], PICColorPicker.prototype, "btnColor", void 0);
__decorate([
    query('#presetBtn')
], PICColorPicker.prototype, "presetBtn", void 0);
__decorate([
    query('.presetsColors')
], PICColorPicker.prototype, "presetsColors", void 0);
__decorate([
    query('#colorPicker')
], PICColorPicker.prototype, "colorPicker", void 0);
__decorate([
    query('#presetsColorPicker')
], PICColorPicker.prototype, "presetsColorPicker", void 0);
__decorate([
    query('button.colorPicker')
], PICColorPicker.prototype, "outerBtn", void 0);
__decorate([
    property({ converter: arrayDecoder, attribute: 'presets' })
], PICColorPicker.prototype, "presets", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: 'presetsonly' })
], PICColorPicker.prototype, "presetsOnly", void 0);
__decorate([
    property({ attribute: 'historylength', type: Number, converter: numberDecoder })
], PICColorPicker.prototype, "historyLength", void 0);
PICColorPicker = __decorate([
    customElement('pic-colorpicker')
], PICColorPicker);
export { PICColorPicker };
//# sourceMappingURL=PICColorPicker.js.map